import { useEffect, useMemo, useRef } from "react";
import { CircleMarker, MapContainer, Marker, Polyline, TileLayer, Tooltip, useMap } from "react-leaflet";
import L, { latLngBounds } from "leaflet";
import type { LatLngExpression } from "leaflet";
import { containerService } from "@/data/mock-containers";
import type { Container } from "@/types/container";
import type { ContainerPosition } from "@/types/api/containers-recap";
import { generateRealisticRoute } from "@/utils/route-generator";
import { storage } from "@/lib/local-storage";

const ROUTE_COLORS = [
    "#2563eb",
    "#16a34a",
    "#db2777",
    "#f59e0b",
    "#7c3aed",
    "#059669",
    "#dc2626",
    "#0ea5e9",
];

/**
 * Generate a great-circle arc between two points.
 * Produces `segments` intermediate points for a smooth curve.
 */
function greatCircleArc(
    from: [number, number],
    to: [number, number],
    segments = 50
): [number, number][] {
    const toRad = (d: number) => (d * Math.PI) / 180;
    const toDeg = (r: number) => (r * 180) / Math.PI;

    const lat1 = toRad(from[0]);
    const lon1 = toRad(from[1]);
    const lat2 = toRad(to[0]);
    const lon2 = toRad(to[1]);

    const d = 2 * Math.asin(
        Math.sqrt(
            Math.pow(Math.sin((lat1 - lat2) / 2), 2) +
            Math.cos(lat1) * Math.cos(lat2) * Math.pow(Math.sin((lon1 - lon2) / 2), 2)
        )
    );

    // If points are very close, just return a straight line
    if (d < 0.0001) return [from, to];

    const points: [number, number][] = [];
    for (let i = 0; i <= segments; i++) {
        const f = i / segments;
        const A = Math.sin((1 - f) * d) / Math.sin(d);
        const B = Math.sin(f * d) / Math.sin(d);
        const x = A * Math.cos(lat1) * Math.cos(lon1) + B * Math.cos(lat2) * Math.cos(lon2);
        const y = A * Math.cos(lat1) * Math.sin(lon1) + B * Math.cos(lat2) * Math.sin(lon2);
        const z = A * Math.sin(lat1) + B * Math.sin(lat2);
        points.push([toDeg(Math.atan2(z, Math.sqrt(x * x + y * y))), toDeg(Math.atan2(y, x))]);
    }
    return points;
}

function interpolate(a: [number, number], b: [number, number], t: number): [number, number] {
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

function FitBounds({ points }: { points: [number, number][] }) {
    const map = useMap();
    useEffect(() => {
        if (!points.length) return;
        const bounds = latLngBounds(points);
        const timer = setTimeout(() => {
            map.fitBounds(bounds, { padding: [40, 40], maxZoom: 4 });
        }, 50);
        return () => clearTimeout(timer);
    }, [map, JSON.stringify(points)]);
    return null;
}

interface WorldRoutesMapPreviewProps {
    height?: number;
    role?: 'shipper' | 'receiver' | 'freight-forwarder';
    accountId?: string;
    /** API data for container positions - when provided, uses this instead of mock data */
    apiPositions?: ContainerPosition[];
}

export function WorldRoutesMapPreview({ height = 360, role = 'shipper', accountId: explicitId, apiPositions }: WorldRoutesMapPreviewProps) {
    const fallbackId = role === 'shipper' ? '1' : role === 'receiver' ? '2' : '3';
    const accountId = explicitId || (storage.getItem('accountId') || fallbackId);

    // Use API positions if provided, otherwise fall back to mock data
    const containers: Container[] = useMemo(() => {
        // If API positions are provided, don't use mock data
        if (apiPositions && apiPositions.length > 0) {
            return []; // Will be handled separately
        }

        // Fallback to mock data
        const all = containerService.getAll();
        if (role === 'shipper') {
            return all.filter(c => c.shipperId === accountId);
        } else if (role === 'receiver') {
            return all.filter(c => c.receiverId === accountId);
        } else {
            // Freight Forwarder: mostra container con bookingNumber
            return all.filter(c => c.bookingNumber !== undefined && c.bookingNumber !== null && c.bookingNumber !== '');
        }
    }, [accountId, role, apiPositions]);

    // Routes from mock data (when API positions not provided)
    const routes = useMemo(() => {
        if (apiPositions && apiPositions.length > 0) {
            return []; // Use API markers instead
        }
        return containers
            .map((c, idx) => {
                const waypoints = c.waypoints && c.waypoints.length > 0
                    ? c.waypoints
                    : generateRealisticRoute(c.route.from, c.route.to, c.createdAt);
                if (waypoints.length === 0) return null;
                const coordinates = waypoints.map(w => [w.lat, w.lng] as [number, number]);
                const color = ROUTE_COLORS[idx % ROUTE_COLORS.length];
                return { c, coordinates, color } as const;
            })
            .filter(Boolean) as Array<{ c: Container; coordinates: [number, number][]; color: string }>;
    }, [containers, apiPositions]);

    // API route lines: origin → current position → destination (great-circle arcs)
    const apiRoutes = useMemo(() => {
        if (!apiPositions || apiPositions.length === 0) return [];
        return apiPositions
            .map((p, idx) => {
                const origin = p.origin_port_lat != null && p.origin_port_lon != null
                    ? [p.origin_port_lat, p.origin_port_lon] as [number, number]
                    : null;
                const dest = p.destination_port_lat != null && p.destination_port_lon != null
                    ? [p.destination_port_lat, p.destination_port_lon] as [number, number]
                    : null;
                const current = p.latitude != null && p.longitude != null
                    ? [p.latitude, p.longitude] as [number, number]
                    : null;

                // Need at least origin and destination to draw a route
                if (!origin && !dest) return null;

                const color = ROUTE_COLORS[idx % ROUTE_COLORS.length];

                // Build the arc segments
                const arcPoints: [number, number][] = [];
                if (origin && dest) {
                    arcPoints.push(...greatCircleArc(origin, dest, 50));
                } else if (origin && current) {
                    arcPoints.push(...greatCircleArc(origin, current, 30));
                } else if (current && dest) {
                    arcPoints.push(...greatCircleArc(current, dest, 30));
                }

                return {
                    id: p.container_id,
                    containerNumber: p.container_number,
                    color,
                    arcPoints,
                    origin,
                    dest,
                    current,
                };
            })
            .filter(Boolean) as Array<{
                id: string;
                containerNumber: string;
                color: string;
                arcPoints: [number, number][];
                origin: [number, number] | null;
                dest: [number, number] | null;
                current: [number, number] | null;
            }>;
    }, [apiPositions]);

    // API positions as markers (current position)
    const apiMarkers = useMemo(() => {
        if (!apiPositions || apiPositions.length === 0) return [];
        return apiPositions
            .filter(p => p.latitude != null && p.longitude != null)
            .map((p, idx) => ({
                id: p.container_id,
                position: [p.latitude!, p.longitude!] as [number, number],
                color: ROUTE_COLORS[idx % ROUTE_COLORS.length],
                containerNumber: p.container_number,
                status: p.status,
                riskLevel: p.risk_level,
            }));
    }, [apiPositions]);

    // All points for bounds fitting (include route endpoints)
    const allPoints = useMemo(() => {
        if (apiRoutes.length > 0) {
            const pts: [number, number][] = [];
            for (const r of apiRoutes) {
                if (r.origin) pts.push(r.origin);
                if (r.dest) pts.push(r.dest);
                if (r.current) pts.push(r.current);
            }
            // Also include markers without routes
            for (const m of apiMarkers) {
                pts.push(m.position);
            }
            return pts.length > 0 ? pts : apiMarkers.map(m => m.position);
        }
        if (apiMarkers.length > 0) {
            return apiMarkers.map(m => m.position);
        }
        return routes.flatMap(r => r.coordinates);
    }, [routes, apiMarkers, apiRoutes]);

    const mapDivRef = useRef<HTMLDivElement | null>(null);

    // Check if we have API data but no valid markers or routes (empty state)
    const isEmptyState = apiPositions !== undefined && apiMarkers.length === 0 && apiRoutes.length === 0;

    return (
        <div ref={mapDivRef} style={{ height, width: '100%' }} className="relative rounded-xl overflow-hidden ring-1 ring-secondary bg-secondary">
            {/* Empty state overlay */}
            {isEmptyState && (
                <div className="absolute inset-0 z-[1000] flex flex-col items-center justify-center bg-primary/80 backdrop-blur-sm">
                    <div className="flex flex-col items-center gap-4 text-center p-6">
                        <div className="rounded-full bg-secondary p-4">
                            <svg className="size-12 text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l5.447 2.724A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-primary mb-1">Nessun container con posizione</h3>
                            <p className="text-sm text-tertiary max-w-xs">
                                I container in questo periodo non hanno ancora dati di tracciamento disponibili
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <MapContainer style={{ height: "100%", width: "100%" }} center={[20, 0]} zoom={2} worldCopyJump scrollWheelZoom>
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {/* API route arcs - great circle lines between origin → destination */}
                {apiRoutes.map((route) => (
                    <span key={`route-group-${route.id}`}>
                        {/* Route arc line */}
                        {route.arcPoints.length > 1 && (
                            <Polyline
                                key={`arc-${route.id}`}
                                positions={route.arcPoints}
                                pathOptions={{
                                    color: route.color,
                                    weight: 2,
                                    opacity: 0.5,
                                    dashArray: "6 4",
                                }}
                            />
                        )}
                        {/* Origin port marker */}
                        {route.origin && (
                            <CircleMarker
                                key={`origin-${route.id}`}
                                center={route.origin}
                                radius={4}
                                pathOptions={{
                                    color: route.color,
                                    fillColor: "#fff",
                                    fillOpacity: 1,
                                    weight: 2,
                                }}
                            />
                        )}
                        {/* Destination port marker */}
                        {route.dest && (
                            <CircleMarker
                                key={`dest-${route.id}`}
                                center={route.dest}
                                radius={4}
                                pathOptions={{
                                    color: route.color,
                                    fillColor: route.color,
                                    fillOpacity: 1,
                                    weight: 2,
                                }}
                            />
                        )}
                    </span>
                ))}

                {/* API markers - current container position (pin icon) */}
                {apiMarkers.map((marker) => {
                    const icon = L.divIcon({
                        html: `<svg width="20" height="24" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg"><path d="M16 0C7.163 0 0 7.163 0 16C0 24.837 16 40 16 40C16 40 32 24.837 32 16C32 7.163 24.837 0 16 0Z" fill="${marker.color}"/><circle cx="16" cy="16" r="5" fill="white"/></svg>`,
                        className: "",
                        iconSize: [20, 24],
                        iconAnchor: [10, 24],
                    });
                    return (
                        <Marker key={`api-marker-${marker.id}`} position={marker.position} icon={icon}>
                            <Tooltip direction="top" offset={[0, -24]}>
                                <span className="text-xs font-medium">{marker.containerNumber}</span>
                            </Tooltip>
                        </Marker>
                    );
                })}

                {/* Mock data markers - when API positions not provided */}
                {routes.map(({ c, coordinates, color }) => {
                    // posizione a ~50% della rotta per preview
                    let totalDistance = 0;
                    const segmentDistances: number[] = [];
                    for (let i = 0; i < coordinates.length - 1; i++) {
                        const dLat = coordinates[i + 1][0] - coordinates[i][0];
                        const dLng = coordinates[i + 1][1] - coordinates[i][1];
                        const dist = Math.sqrt(dLat * dLat + dLng * dLng);
                        segmentDistances.push(dist);
                        totalDistance += dist;
                    }
                    let shipPos: LatLngExpression = coordinates[0];
                    if (coordinates.length >= 2 && totalDistance > 0) {
                        const targetDistance = 0.5 * totalDistance;
                        let acc = 0;
                        for (let i = 0; i < segmentDistances.length; i++) {
                            const seg = segmentDistances[i];
                            if (seg === 0) continue;
                            if (acc + seg >= targetDistance) {
                                const segmentT = (targetDistance - acc) / seg;
                                shipPos = interpolate(coordinates[i], coordinates[i + 1], segmentT) as LatLngExpression;
                                break;
                            }
                            acc += seg;
                            if (i === segmentDistances.length - 1) shipPos = coordinates[coordinates.length - 1];
                        }
                    }
                    const [lat, lng] = shipPos as [number, number];
                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

                    const icon = L.divIcon({
                        html: `<svg width="20" height="24" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg"><path d="M16 0C7.163 0 0 7.163 0 16C0 24.837 16 40 16 40C16 40 32 24.837 32 16C32 7.163 24.837 0 16 0Z" fill="${color}"/><circle cx="16" cy="16" r="5" fill="white"/></svg>`,
                        className: "",
                        iconSize: [20, 24],
                        iconAnchor: [10, 24],
                    });
                    return <Marker key={`marker-${c.id}`} position={shipPos} icon={icon} />;
                })}

                <FitBounds points={allPoints} />
            </MapContainer>
        </div>
    );
}

export default WorldRoutesMapPreview;
